/**
 * MODULE: domain/pages/frontend/utils/blockInsertUtils.ts
 * PURPOSE: Insert a new block below the hovered block handle target (Notion-style + menu)
 * PUBLIC_API: focusEmptyBlockBelow, insertBlockBelowTarget
 * SIDE_EFFECTS: TipTap document mutations
 * RECENT: 2026-05-24 [2.2.1186] — insert block below drag-handle target
 */

import type { Editor } from '@tiptap/react';
import type { SlashCommandId } from './slashCommands';
import { runSlashCommand, slashCommandToAiPreset } from './slashCommands';
import type { DraggableBlockTarget } from './pagesBlockDragTarget';

export type BlockAddCommandCallbacks = {
  onUploadRequest: (kind: 'image' | 'file') => void;
  onPageLinkRequest: () => void;
  onBookmarkRequest: () => void;
  onActionButtonRequest: () => void;
  onDatabaseEmbedRequest: () => void;
  onTasksSyncRequest: () => void;
  onTranscriptPreviewRequest: () => void;
  onDealPreviewRequest: () => void;
  onContactPreviewRequest: () => void;
  onCompanyPreviewRequest: () => void;
  onAiCommandRequest: (preset: 'summarise' | 'draft_email' | 'call_prep' | 'custom') => void;
};

/** Move the cursor into a new empty paragraph directly below the target block. */
export function focusEmptyBlockBelow(editor: Editor, target: DraggableBlockTarget): void {
  const insertPos = target.pos + target.node.nodeSize;
  editor
    .chain()
    .focus()
    .insertContentAt(insertPos, { type: 'paragraph' })
    .setTextSelection(insertPos + 1)
    .run();
}

/** Run a slash-style block command on a fresh line below the hovered block. */
export function insertBlockBelowTarget(
  editor: Editor,
  target: DraggableBlockTarget,
  commandId: SlashCommandId,
  callbacks: BlockAddCommandCallbacks
): void {
  focusEmptyBlockBelow(editor, target);

  if (commandId === 'image' || commandId === 'file') {
    callbacks.onUploadRequest(commandId);
    return;
  }
  if (commandId === 'page_link') {
    callbacks.onPageLinkRequest();
    return;
  }
  if (commandId === 'bookmark') {
    callbacks.onBookmarkRequest();
    return;
  }
  if (commandId === 'action_button') {
    callbacks.onActionButtonRequest();
    return;
  }
  if (commandId === 'database_embed') {
    callbacks.onDatabaseEmbedRequest();
    return;
  }
  if (commandId === 'tasks_sync') {
    callbacks.onTasksSyncRequest();
    return;
  }
  if (commandId === 'transcript_preview') {
    callbacks.onTranscriptPreviewRequest();
    return;
  }
  if (commandId === 'crm_deal_preview') {
    callbacks.onDealPreviewRequest();
    return;
  }
  if (commandId === 'crm_contact_preview') {
    callbacks.onContactPreviewRequest();
    return;
  }
  if (commandId === 'crm_company_preview') {
    callbacks.onCompanyPreviewRequest();
    return;
  }

  const aiPreset = slashCommandToAiPreset(commandId);
  if (aiPreset) {
    callbacks.onAiCommandRequest(aiPreset);
    return;
  }

  runSlashCommand(editor, commandId);
}
